/**
 * Global data-scope: the (locality, time-range) pair every aggregate page reads.
 *
 * The **URL search params are the single source of truth** (`?locality=…&days=…`),
 * so a scoped view is shareable, bookmarkable, and survives refresh + back/forward.
 * The last-used scope is mirrored to localStorage so navigating (via a plain
 * `NavLink`, which drops the query) to a param-less page inherits it instead of
 * snapping back to defaults. `useScope()` is the facade the rest of the app uses;
 * nothing else touches the params directly.
 */
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";
import { routeScope } from "@/features/registry";

import {
  ALL_LOCALITIES,
  DAYS_PARAM,
  defaultDays,
  LOCALITY_PARAM,
  SCOPE_STORAGE_KEY,
  normalizeLocality,
  parseDays,
  type DayRange,
} from "@/scope/constants";

interface Scope {
  /** Selected locality name, or `null` for the system-wide view. */
  locality: string | null;
  /** Selected time window. */
  days: DayRange;
  setLocality: (value: string | null) => void;
  setDays: (value: DayRange) => void;
}

const ScopeContext = createContext<Scope | null>(null);

interface StoredScope {
  locality?: string | null;
  days?: string;
}

function readStored(): StoredScope {
  try {
    const raw = window.localStorage.getItem(SCOPE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredScope) : {};
  } catch {
    return {};
  }
}

function writeStored(scope: { locality: string | null; days: DayRange }): void {
  try {
    window.localStorage.setItem(
      SCOPE_STORAGE_KEY,
      JSON.stringify({ locality: scope.locality, days: String(scope.days) }),
    );
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const stored = readStored();

  // Scope is derived from the URL each render (source of truth), with the stored
  // last-used scope as the fallback only when a param is *absent* (a fresh
  // navigation). `has` — not a null `get` — makes that distinction: `?locality=all`
  // is a deliberate system-wide pick and must beat the stored locality.
  // A route may decline to inherit the remembered locality (see ScopeControls):
  // its figures are read against the system-wide dashboard, so silently carrying a
  // locality over would show the same metric with a different value than the page
  // the user arrived from. An explicit `?locality=` still wins, so a pick made on
  // such a page — and a shared link — behaves normally.
  const route = routeScope(pathname);
  const inheritsLocality = route?.inheritLocality !== false;
  const systemWide = route?.systemWide === true;
  const locality = normalizeLocality(
    searchParams.has(LOCALITY_PARAM)
      ? searchParams.get(LOCALITY_PARAM)
      : inheritsLocality
        ? (stored.locality ?? null)
        : null,
  );
  const days = parseDays(
    searchParams.has(DAYS_PARAM) ? searchParams.get(DAYS_PARAM) : stored.days,
  );

  const commit = useCallback(
    (patch: { locality?: string | null; days?: DayRange }) => {
      const next = new URLSearchParams(searchParams);
      if ("locality" in patch) {
        next.set(LOCALITY_PARAM, normalizeLocality(patch.locality) ?? ALL_LOCALITIES);
      }
      if ("days" in patch && patch.days != null) {
        next.set(DAYS_PARAM, String(patch.days));
      }
      // Navigate rather than `setSearchParams`, which goes to a bare "?query" and drops
      // the URL fragment — and with it every `#anchor` deep link (the glossary's).
      navigate({ pathname, search: `?${next}`, hash }, { replace: true });
    },
    [navigate, pathname, hash, searchParams],
  );

  // Persist the effective scope, and re-hydrate the URL after a param-less
  // navigation so the new page is scoped + the link stays shareable. Not until
  // the config has loaded: the default window is the server's, and stamping the
  // boot-time fallback into the URL would pin it there after hydration.
  const { loading: configLoading } = useAuth();
  useEffect(() => {
    if (configLoading) return;
    // A system-wide route (the dashboard) is the starting point for the pages it links
    // to: it does not read the scope, it sets it — all localities, default window — so
    // those pages open on the figures the dashboard just showed. Nothing is written to
    // its own URL: it has no scope controls to keep in sync with one.
    if (systemWide) {
      writeStored({ locality: null, days: defaultDays() });
      return;
    }
    // A non-inheriting route keeps its locality to itself: persisting `null` here
    // would reset the remembered locality for every page that does share it, so
    // merely visiting this page would silently widen the others.
    writeStored({
      locality: inheritsLocality ? locality : (readStored().locality ?? null),
      days,
    });
    const needsDays = !searchParams.has(DAYS_PARAM);
    const needsLocality = !searchParams.has(LOCALITY_PARAM) && inheritsLocality;
    if (needsDays || needsLocality) {
      commit(needsLocality ? { locality, days } : { days });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locality, days, searchParams, inheritsLocality, systemWide, configLoading]);

  const value = useMemo<Scope>(
    () => ({
      locality,
      days,
      setLocality: (v) => commit({ locality: v }),
      setDays: (v) => commit({ days: v }),
    }),
    [locality, days, commit],
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

/** Read the global scope. Falls back to defaults outside a provider (e.g. tests). */
// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider
export function useScope(): Scope {
  const ctx = useContext(ScopeContext);
  if (ctx) return ctx;
  return {
    locality: null,
    days: defaultDays(),
    setLocality: () => {},
    setDays: () => {},
  };
}
